// src/components/ContentBuilderPage.js
import React, { useRef, useState, useEffect } from "react";
import { Container, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useLessonEditor } from "../hooks/useLessonEditor";
import { CourseLessonSelector } from "./CourseLessonSelector";
import { InstructionBanner } from "./InstructionBanner";
import { LessonPreview } from "./LessonPreview";
import { VizCard }      from "./VizCard";
import { useAxiosAuth } from "../hooks/useAxiosAuth";
import EditPromptDialog from "./EditPromptDialog";
import { useCredits } from "../context/CreditsContext";
import { useAssetList } from "../hooks/useAssetList";
import styles from "../styles/contentBuilderStyles";
import { serializeCleanHTML, injectOverlaysIntoBody } from "../utils/htmlUtils";

export function ContentBuilderPage({
  instructionText,
  modal,
  generatorFn,   // async → either { gcs_link, … } OR { visual_id, gcs_link, … }
  getAnchorLabel = (viz) => "Open visualization in new tab",
  assetType = "visuals",
  createBtnTxt = "Create Visual Using Prompt",
}) {
  const editor = useLessonEditor();
  const iframeRef = useRef(null);
  const { refreshCredits } = useCredits();
  const axios = useAxiosAuth();

  // fetch existing assets (visuals, quizzes, etc.) for edit mode
  const {
     items: assetItems,
     byRel,
     refresh: refreshAssets,   // ← server refetch
     upsert:  upsertAsset,     // ← optimistic local patch
  } = useAssetList(
    assetType,
    editor.selectedCourse,
    editor.selectedLesson
  );

  const [selText,    setSelText]    = useState("");
  const [selRange,   setSelRange]   = useState(null);
  const [modalOpen,  setModalOpen]  = useState(false);

  const [editOpen,   setEditOpen]   = useState(false);
  const [editingViz, setEditingViz] = useState(null);
  const [refining,   setRefining]   = useState(false);

  // —— new state for visuals —— 
  const [visuals,           setVisuals]         = useState([]);
  const [replacements,      setReplacements]    = useState([]);
  const [pendingPlacement,  setPendingPlacement]= useState(null);
  const [editVisualMode,    setEditVisualMode]  = useState(false);
  const placeRangeRef      = useRef(null);
  const [confirmOpen,     setConfirmOpen]     = useState(false);   // dialog visible?
  const [pendingDeleteId, setPendingDeleteId] = useState(null);    // which asset?

  const openEditDialog  = (viz) => { setEditingViz(viz); setEditOpen(true); };
  const closeEditDialog = ()    => { setEditOpen(false); setEditingViz(null); };

  const resolveLink = gcs => {
    const base = editor.baseUrl;
    return `${base}${gcs}`;
  };

  // utility to find both iframe and anchor elements by visual id
  const getVisualNodes = (doc, id) => ({
    iframeEl: doc.querySelector(`iframe[data-visual-id="${id}"]`) || null,
    anchorEl: doc.querySelector(`a[data-visual-id="${id}"]`)      || null,
  });

  // 2️⃣ when user hits “Generate…” in the modal
  const handleGenerate = async (updatedText, extraArgs) => {
    try {
      const payload = await generatorFn(
        updatedText,
        editor.selectedCourse,
        editor.selectedLesson,
        extraArgs
      );

      // ——— visual workflow ———
      if (payload.visual_id) {
        setVisuals(vs => [
          ...vs,
          {
            id: payload.visual_id,
            prompt: updatedText,
            rawLink: payload.gcs_link,
            displayLink: resolveLink(payload.gcs_link),
            extraArgs: extraArgs,
            placed: false               // NEW: not yet embedded
          }
        ]);
      }
      refreshCredits();
    } catch(err) {
      console.error(err);
      // enqueueSnackbar("Failed to generate visual", {variant:"error"});
    } finally {
      setModalOpen(false);
    }
  };

  /* ────────────────────────────────────────────────────────────────
     Regeneration respects the `placed` flag.
  ────────────────────────────────────────────────────────────────── */
// inside ContentBuilderPage.jsx
const handleRegenerate = async (newPrompt, accuracy_level) => {
  if (!editingViz) return;

  setRefining(true);
  try {
    const { data } = await axios.post(`/${assetType}/refine`, {
      visual_id:     editingViz.id,
      new_prompt:    newPrompt,
      accuracy_level
    });

    if (editingViz.placed) {
      // already embedded ⇒ queue a replacement card
      setReplacements(rs => [
        // ensure only one pending replacement per id
        ...rs.filter(r => r.id !== editingViz.id),
        {
          id:          editingViz.id,
          prompt:      newPrompt,
          rawLink:     data.gcs_link,
          displayLink: resolveLink(data.gcs_link),
          placed: true
        }
      ]);
    } else {
      // not placed yet ⇒ mutate the pending-visuals list
      setVisuals(vs =>
        vs.map(v =>
          v.id === editingViz.id
            ? {
                ...v,
                prompt:      newPrompt,
                rawLink:     data.gcs_link,
                displayLink: resolveLink(data.gcs_link),
              }
            : v
        )
      );
    }

    closeEditDialog();
    refreshCredits();
  } catch (err) {
    console.error(err);
  } finally {
    setRefining(false);
  }
};

  // 4️⃣ discard
  const handleDiscard = id =>
    setVisuals(vs => vs.filter(v => v.id !== id));

  // 5️⃣ place: kick off “placement mode”
  const handlePlaceClick = (id) => {
    setPendingPlacement(id);
    placeRangeRef.current = selRange;
  };

  // selection handler – open the tabbed dialog directly
  const handleOverlaySelect = ({ id, iframeEl }) => {
    const prompt =
      visuals.find(v => v.id === id)?.prompt ||
      replacements.find(r => r.id === id)?.prompt ||
      "";
    openEditDialog({
      id,
      iframeEl,
      prompt,
      rawLink: iframeEl?.src,
      placed: true               // NEW: coming from the document
    });
  };

  const actuallyDeleteVisual = async (id) => {
    try {
      await axios.delete(`/assets/${assetType}/${id}`);
  
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        doc.querySelectorAll(`[data-visual-id="${id}"]`)
           .forEach(el => (el.closest("div") || el).remove());
        editor.saveBody(serializeCleanHTML(doc));
      }
  
      setReplacements(r => r.filter(x => x.id !== id));
      setVisuals(v => v.filter(x => x.id !== id));
      refreshAssets();
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleDeleteRequest = (id) => {
    setPendingDeleteId(id);
    setConfirmOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (pendingDeleteId) await actuallyDeleteVisual(pendingDeleteId);
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };
  
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
  };

  function refreshVisualOverlays(doc, rel, id, byRel) {
    const overlayMap = { ...byRel, [rel]: { visual_id: id } };
    doc.body.innerHTML = injectOverlaysIntoBody(doc.body.innerHTML, overlayMap);
  }
  

  const handleApplyUpdate = (id) => {
    
    const rep = replacements.find(r => r.id === id);
    if (!rep) return;

    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return;
  
      // 1️⃣  fresh references
      const { iframeEl, anchorEl } = getVisualNodes(doc, rep.id);
  
      // 2️⃣  update with *attributes* (keeps them relative)
      if (iframeEl) iframeEl.setAttribute("src",  rep.rawLink);
      if (anchorEl) anchorEl.setAttribute("href", rep.rawLink);
  
      // 3️⃣  rebuild overlays
      const rel        = rep.rawLink.split("/").slice(-2).join("/");
      refreshVisualOverlays(doc, rel, rep.id, byRel);

  
      // 4️⃣  mark dirty & clear card
      editor.saveBody(serializeCleanHTML(doc));
      setReplacements((rs) => rs.filter((r) => r.id !== rep.id));
      refreshAssets();
    } catch (err) {
      console.error(err);
    }
  };
  

  const handleDiscardReplacement = async (id) => {
    // find the queued replacement so we can update the DOM after reverting
    const rep = replacements.find(r => r.id === id);
    if (!rep) return;

    try {
      // 1) trigger revert on the backend
      await axios.post(`/${assetType}/${id}/revert`);
      // backend returns a *relative* path we need to re-resolve
    } catch (err) {
      console.error(err);
      // enqueueSnackbar("Failed to revert visual", {variant:"error"});
    } finally {
      // 4) remove the replacement card either way
      setReplacements(rs => rs.filter(r => r.id !== id));
    }
  };

/* 6️⃣ when in placement mode, watch for the user’s next click inside the iframe */
useEffect(() => {
  if (!pendingPlacement) return;
  const doc = iframeRef.current?.contentDocument;
  if (!doc) return;

  const onClick = async (e) => {
    e.preventDefault();
    const viz = visuals.find(v => v.id === pendingPlacement);
    if (!viz) return;

    // 1️⃣ compute caret range
    let range;
    if (typeof doc.caretRangeFromPoint === "function") {
      range = doc.caretRangeFromPoint(e.clientX, e.clientY);
    } else if (typeof doc.caretPositionFromPoint === "function") {
      const pos = doc.caretPositionFromPoint(e.clientX, e.clientY);
      range = doc.createRange();
      range.setStart(pos.offsetNode, pos.offset);
    }
    if (!range) return;

    /* --------------------------------------------------------------
       NEW: find the nearest block-level element that encloses
       the caret, then insert *after* that element so we don’t
       break paragraphs, headings, list items, etc.
    -------------------------------------------------------------- */
    const BLOCK_SELECTOR =
      "p,h1,h2,h3,h4,h5,h6,li,ul,ol,div,section,article,aside,blockquote,figure,pre";

    let blockEl =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : range.startContainer;
    blockEl = blockEl.closest(BLOCK_SELECTOR) || blockEl;

    // 2️⃣ build the visual container
    const container = doc.createElement("div");
    container.style.margin = "12px 0";
    const labelText = getAnchorLabel(viz);
    container.innerHTML = `
      <a href="${viz.rawLink}" target="_blank">
        ${labelText}
      </a>
      <iframe
        src="${viz.rawLink}"
        width="100%"
        height="400"
        style="border:0"
        data-visual-id="${viz.id}"
      ></iframe>
    `;

    // 3️⃣ insert after the enclosing block element
    blockEl.insertAdjacentElement("afterend", container);

    // 4️⃣ housekeeping (unchanged)
    upsertAsset({ visual_id: viz.id, gcs_link: viz.rawLink, prompt: viz.prompt });
    const rel = viz.rawLink.split("/").slice(-2).join("/");
    refreshVisualOverlays(doc, rel, viz.id, byRel);
    editor.saveBody(serializeCleanHTML(doc));

    setVisuals(vs => vs.filter(v => v.id !== pendingPlacement));
    setPendingPlacement(null);
    setSelRange(null);
    doc.removeEventListener("click", onClick, true);
  };

  doc.addEventListener("click", onClick, true);
  return () => doc.removeEventListener("click", onClick, true);
}, [pendingPlacement]);

  return (
    <Container maxWidth="lg">
      {/* 1) show any outstanding VizCards */}
      {visuals.map(viz => (
        <VizCard
          key={viz.id}
          viz={viz}
          onPlace={()   => handlePlaceClick(viz.id)}
          onEdit={openEditDialog} 
          onDiscard={()=> handleDiscard(viz.id)}
        />
      ))}

      {/* B) pending replacements for already-placed visuals */}
      {replacements.map(rep => (
        <VizCard
          key={`rep-${rep.id}`}
          viz={rep}
          onApply={() => handleApplyUpdate(rep.id)}
          onEdit={openEditDialog}
          onDiscard={() => handleDiscardReplacement(rep.id)}
        />
      ))}

      {/* 2) banner when in placement-mode */}
      {pendingPlacement && (
        <div style={styles.instructionBanner}>
          Click in the preview where you want to insert your visualization.
        </div>
      )}

      <CourseLessonSelector editor={editor} />

      {/* InstructionBanner, launch button, and toolbar on one line */}
      <div style={styles.bannerRow}>
        <InstructionBanner show>
          {instructionText}
        </InstructionBanner>
        <button
          style={styles.button}
          onClick={() => {
            setSelText("");
            setModalOpen(true);
          }}
        >
          {createBtnTxt}
        </button>
        <button
          style={{ ...styles.button, background: editVisualMode ? "#0056b3" : "#9e9e9e" }}
          onClick={() => setEditVisualMode(v => !v)}
        >
          {editVisualMode ? "Exit visual-edit mode" : "Edit existing visual"}
        </button>
      </div>

      <LessonPreview
        iframeRef={iframeRef}
        headContent={editor.head}
        bodyContent={editor.body}
        baseUrl={editor.baseUrl}
        editVisualMode={editVisualMode}
        onVisualSelect={handleOverlaySelect} 
        pendingPlacement={pendingPlacement}
        onTextSelect={(text, range) => {
          if (pendingPlacement) return;
          setSelText(text);
          setSelRange(range);
          setModalOpen(true);
        }}
        styles={styles}
        byRel={byRel}
      />
      <Dialog open={confirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete {assetType.slice(0, -1)}?</DialogTitle>
        <DialogContent>
          This will permanently remove the selected {assetType.slice(0, -1)} from the lesson.
          You can’t undo this action.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
      {React.cloneElement(modal, {
        open: modalOpen,
        initialText: selText,
        onCancel: () => setModalOpen(false),
        onGenerate: handleGenerate,
      })}
      <EditPromptDialog
        open={editOpen}
        initialPrompt={editingViz?.prompt}
        initialSize={{
          w: editingViz?.iframeEl?.getAttribute("width")  || "100%",
          h: editingViz?.iframeEl?.getAttribute("height") || "400",
        }}
        busy={refining}
        onCancel={closeEditDialog}
        onRegenerate={handleRegenerate}
        onResize={({ w, h }) => {
          const iframe = editingViz?.iframeEl;
          if (!iframe) return;

          /* —— keep attributes for backwards-compat, but also inline-style —— */
          iframe.setAttribute("width",  w);
          iframe.setAttribute("height", h);

          // ensure centering & responsive sizing via inline CSS
          iframe.style.display = "block";
          iframe.style.margin  = "0 auto";
          iframe.style.width   = w;
          iframe.style.height  = h;

          editor.saveBody(serializeCleanHTML(iframe.ownerDocument));
          closeEditDialog();
        }}
        onDelete={() => {
          handleDeleteRequest(editingViz.id);
          closeEditDialog();
        }}
      />
    </Container>
  );
}
